import { useTranslation } from 'react-i18next';
import { AccountFundingOption, FieldData, formattedCurrency, FundingTrigger, sanitizeToNumeric } from 'vopay';
import { PaymentFieldDefault } from './types';
import { getAvailableBalance, isPaymentScheduled, useFundingOptionField } from './useFundingOptionField';

export const usePayFromFields = (
  fundingOptions: AccountFundingOption[],
  isFetchingFundingOptions: boolean,
  // Pay From is only ever shown when payments are drawn from the VoPay balance by default —
  // the other triggers fund every payment from the bank account automatically and hide this field.
  fundingTrigger: FundingTrigger,
): FieldData<PaymentFieldDefault>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });

  const getSubtitle = (option: AccountFundingOption) => {
    if (option.Source === 'balance') {
      return `${option.Currency} ${formattedCurrency(String(getAvailableBalance(option)))}`;
    }
    return t('Fields.PayFrom.fundThenPay', { defaultValue: 'Fund the balance first, then pay' });
  };

  return useFundingOptionField(fundingOptions, isFetchingFundingOptions, {
    fieldName: 'PayFrom',
    translationPrefix: 'PayFrom',
    labelDefaultValue: 'Pay From',
    showFee: true,
    showSettlementTime: true,
    showBalanceInlineSubtitle: true,
    getSubtitle,
    // Also hidden when there's no real choice (fundingOptions collapses to just 'balance').
    // Applies to Request the same as Provide — the Request flow funds the account from the
    // selected source client-side and hands the resulting transaction as ParentTransactionID,
    // same as the standalone eLinx Pay page (ElinxPayContainer.tsx).
    isAdditionallyVisible: (values) => fundingTrigger === 'never' && !isPaymentScheduled(values) && fundingOptions.length > 1,
    isOptionDisabled: (option, values) =>
      option.Source === 'balance' &&
      !!values.Amount &&
      Number(sanitizeToNumeric(values.Amount)) > getAvailableBalance(option),
  });
};
